import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EmbedLogo from '../components/svg/EmbedLogo';
import { toast } from '../hooks/use-toast';
import { useEmbedErrorDetection } from '../hooks/useEmbedErrorDetection';
import { getEnvironment } from '../utils/environment';

export default function Home() {
  const { tenantId, entryPointCode, skeleton } = useParams<{
    tenantId: string;
    entryPointCode: string;
    skeleton: string;
  }>();

  const environment = getEnvironment();

  const error = useEmbedErrorDetection(tenantId, false);

  const [isSharedShortMode, setIsSharedShortMode] = useState(
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('shortId')
  );

  useEffect(() => {
    if (!isSharedShortMode) return;

    const startedAt = Date.now();

    const interval = window.setInterval(() => {
      const stillHasShortId = new URLSearchParams(window.location.search).has('shortId');

      // SDK clears ?shortId=... after opening the shared short.
      // Fallback timeout prevents getting stuck hidden if that never happens.
      if (!stillHasShortId || Date.now() - startedAt > 3000) {
        setIsSharedShortMode(false);
        window.clearInterval(interval);
      }
    }, 50);

    return () => {
      window.clearInterval(interval);
    };
  }, [isSharedShortMode]);

  useEffect(() => {
    if (!tenantId) return;

    localStorage.setItem("horizon_tenant_id", tenantId);
  }, [tenantId]);

  const handleCopyEmbedCode = () => {
    const baseUrl = environment === 'production' ? 'https://play.horizonexp.com' : 'https://play-dev.macintosh-ix88.thinkflagship.com';
    if (!tenantId || !entryPointCode) return;
    const embedCode =
      `<div data-horizon-ep="#${entryPointCode}"></div>
<script src="${baseUrl}/embed.js" data-horizon-ref="${tenantId}" data-horizon-ep-skeleton="${skeleton}"></script>`;
    navigator.clipboard.writeText(embedCode);
    toast({
      variant: 'success',
      title: 'Embed code copied to clipboard.'
    });
  };

  // Show error message if error occurred
  if (error) {
    return (
      <section className="w-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg md:text-xl mb-2">
            The link you are trying to reach is not valid.
          </p>
          <p className="text-lg md:text-xl">
            Please check your connection and try again.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full pb-24 sm:pb-16 lg:pb-24 2xl:pb-[96px]">
      <div
        className={isSharedShortMode ? 'opacity-0 pointer-events-none' : ''}
        data-horizon-ep={`#${entryPointCode}`}
        data-horizon-ep-skeleton={skeleton}
      ></div>

      {!isSharedShortMode && (
        <div className='absolute bottom-8 sm:bottom-6 lg:bottom-10 2xl:bottom-12 left-1/2 -translate-x-1/2'>
          <button
            className='bg-primary/10 hover:bg-primary/20 text-primary rounded-full px-4 py-3 flex items-center gap-2 font-semibold text-xs sm:text-sm 2xl:text-lg'
            onClick={() => {
              handleCopyEmbedCode();
            }}
          >
            <span>
              <EmbedLogo />
            </span>
            <span>Embed code</span>
          </button>
        </div>
      )}
    </section>
  );
}